/**
 * EnablementChecklist (J6, Wave 3) — rows from the settings response
 * `enablement[]`: server-computed done/undone verdict icon, label, detail,
 * and an optional deep-link affordance (in-app route via react-router).
 *
 * Frontend never derives done-ness — `done` renders as-is (AC-009 checklist
 * row "Composite OEC pinned (G3)" is done ONLY when the server says the pin
 * exists). Composition per .design/autonomy-facade/mockups/autonomy-page.html.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyEnablementItem } from '../types/autonomy.types';

export interface EnablementChecklistProps {
  items: AutonomyEnablementItem[];
  /** Navigates to the item's deepLink (react-router). */
  onNavigate: (deepLink: string) => void;
}

export const EnablementChecklist: React.FC<EnablementChecklistProps> = ({ items, onNavigate }) => {
  if (items.length === 0) return null;

  return (
    <Paper sx={{ mb: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 500, px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        Enablement checklist
      </Typography>
      {items.map((item) => (
        <Box
          key={item.id}
          data-testid={AUTONOMY_TEST_IDS.checklistItem(item.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          {item.done
            ? <CheckCircleIcon color="success" fontSize="small" />
            : <ErrorIcon color="warning" fontSize="small" />}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1">{item.label}</Typography>
            {item.detail && (
              <Typography variant="body2" color="text.secondary">
                {item.detail}
              </Typography>
            )}
          </Box>
          {item.deepLink && (
            <Button
              variant="text"
              size="small"
              sx={{ whiteSpace: 'nowrap', flex: 'none' }}
              onClick={() => onNavigate(item.deepLink!)}
            >
              Open
            </Button>
          )}
        </Box>
      ))}
    </Paper>
  );
};

export default EnablementChecklist;
